// analytics.ts — unified wrapper for GA4 + PostHog
// GA4:     replace VITE_GA4_MEASUREMENT_ID in .env.local (format: G-XXXXXXXXXX)
// PostHog: replace VITE_POSTHOG_KEY in .env.local (format: phc_XXXXXXXXXX)

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    posthog?: {
      init: (key: string, options: object) => void;
      capture: (event: string, properties?: object) => void;
      identify: (id: string, properties?: object) => void;
      reset: () => void;
    };
  }
}

// ── GA4 ──────────────────────────────────────────────────────────────────────

export function initGA4(measurementId: string) {
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') return;
  // Script is already injected in index.html — just ensure gtag is available
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function (...args) { window.dataLayer!.push(args); };
  }
}

export function trackPageView(path: string, title?: string) {
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
  window.posthog?.capture('$pageview', { path });
}

export function trackEvent(name: string, properties?: Record<string, unknown>) {
  // GA4
  window.gtag?.('event', name, properties);
  // PostHog
  window.posthog?.capture(name, properties);
}

// Key conversion events — call these from components
export const track = {
  quizStarted: () => trackEvent('quiz_started'),
  quizCompleted: (score: number) => trackEvent('quiz_completed', { score }),
  quizEmailCaptured: () => trackEvent('quiz_email_captured'),
  demoRequested: () => trackEvent('demo_requested'),
  signupClicked: (source: string) => trackEvent('signup_clicked', { source }),
  trialStarted: () => trackEvent('trial_started'),
  pricingViewed: () => trackEvent('pricing_viewed'),
};

// ── PostHog ───────────────────────────────────────────────────────────────────

export function initPostHog(apiKey: string) {
  if (!apiKey || apiKey === 'phc_XXXXXXXXXX' || window.posthog?.capture) return;
  const script = document.createElement('script');
  script.innerHTML = `
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]);t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+" (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    posthog.init('${apiKey}', {api_host:'https://app.posthog.com', capture_pageview: false});
  `;
  document.head.appendChild(script);
}

export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  window.posthog?.identify(userId, properties);
}
